import { useState } from 'react';
import { ChevronRight, File, Folder, GitBranch, RefreshCw, Star, CircleDot } from 'lucide-react';
import { useRepoSnapshots, type RepoSnapshot } from '../lib/data';
import { SkeletonTable } from './Skeleton';

interface FileEntry { path: string; type: string; size: number }
interface BranchEntry { name: string; sha: string; protected: boolean }
interface IssueEntry { number: number; title: string; state: string; labels: Array<{ name?: string }>; comments: number }

function parse<T>(s: string): T[] {
  try { return JSON.parse(s) as T[]; } catch { return []; }
}

export function Repos() {
  const { snapshots, loading, error, retry } = useRepoSnapshots();
  const [selected, setSelected] = useState<RepoSnapshot | null>(null);
  const [tab, setTab] = useState<'tree' | 'branches' | 'issues' | 'readme'>('tree');

  if (loading) return <SkeletonTable rows={6} />;
  if (error) return (
    <div className="flex flex-col gap-3 p-6">
      <p className="text-sm text-red-500">{error}</p>
      <button onClick={retry} className="text-xs text-accent underline">Retry</button>
    </div>
  );

  const list = snapshots ?? [];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Repositories</h2>
          <p className="text-xs text-muted-foreground">Full snapshots (file tree, branches, issues, README) fetched from GitHub for the agent OS.</p>
        </div>
        <button onClick={retry} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border hover:bg-accent/5">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {list.length === 0 && (
        <div className="text-sm text-muted-foreground py-8 text-center">
          No snapshots yet — install the GitHub App or run a catalog sync.
        </div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0, 320px) minmax(0, 1fr)' }}>
        {/* Repo list */}
        <div className="flex flex-col gap-1.5">
          {list.map((s) => (
            <button
              key={s.id}
              onClick={() => { setSelected(s); setTab('tree'); }}
              className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${selected?.id === s.id ? 'border-accent bg-accent/5' : 'hover:bg-accent/5'}`}
            >
              <div className="font-medium flex items-center gap-1.5">
                <Folder className="w-3.5 h-3.5 text-accent" />
                <span className="truncate">{s.id}</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
                {s.language && <span>{s.language}</span>}
                <span className="flex items-center gap-0.5"><Star className="w-3 h-3" />{s.stars}</span>
                <span className="flex items-center gap-0.5"><CircleDot className="w-3 h-3" />{s.openIssues}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Detail pane */}
        {selected && (
          <div className="border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b bg-accent/5">
              <div>
                <div className="font-semibold text-sm">{selected.id}</div>
                <div className="text-[10px] text-muted-foreground">
                  {selected.defaultBranch ?? ''} · synced {new Date(selected.syncedAt).toLocaleString()}
                  {selected.private ? ' · private' : ''}
                </div>
              </div>
              <div className="flex gap-1">
                {(['tree', 'branches', 'issues', 'readme'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-md ${tab === t ? 'bg-accent text-accent-fg' : 'hover:bg-accent/5'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 max-h-[560px] overflow-auto text-xs font-mono">
              {tab === 'tree' && (
                <div className="flex flex-col">
                  {parse<FileEntry>(selected.fileTree).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 py-0.5">
                      {f.type === 'tree'
                        ? <Folder className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        : <File className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                      <span className="truncate">{f.path}</span>
                      {f.type === 'blob' && f.size > 0 && (
                        <span className="ml-auto text-muted-foreground text-[10px]">{f.size.toLocaleString()} B</span>
                      )}
                    </div>
                  ))}
                  {selected.fileTree === '[]' && <span className="text-muted-foreground">No file tree captured.</span>}
                </div>
              )}
              {tab === 'branches' && (
                <div className="flex flex-col">
                  {parse<BranchEntry>(selected.branches).map((b, i) => (
                    <div key={i} className="flex items-center gap-2 py-0.5">
                      <GitBranch className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <span className="font-medium">{b.name}</span>
                      {b.protected && <span className="text-[9px] text-amber-500 border border-amber-500/40 rounded px-1">protected</span>}
                      <span className="ml-auto text-muted-foreground text-[10px] truncate max-w-[220px]">{b.sha}</span>
                    </div>
                  ))}
                  {selected.branches === '[]' && <span className="text-muted-foreground">No branches captured.</span>}
                </div>
              )}
              {tab === 'issues' && (
                <div className="flex flex-col">
                  {parse<IssueEntry>(selected.issues).map((i) => (
                    <div key={i.number} className="flex items-center gap-2 py-1">
                      <CircleDot className={`w-3.5 h-3.5 shrink-0 ${i.state === 'open' ? 'text-emerald-500' : 'text-slate-400'}`} />
                      <span className="text-muted-foreground">#{i.number}</span>
                      <span className="truncate">{i.title}</span>
                      {i.labels.slice(0, 3).map((l, j) => (
                        <span key={j} className="text-[9px] px-1.5 rounded-full bg-accent/10 text-accent">{l.name ?? String(l)}</span>
                      ))}
                      <ChevronRight className="w-3 h-3 text-muted-foreground ml-auto" />
                    </div>
                  ))}
                  {selected.issues === '[]' && <span className="text-muted-foreground">No open issues captured.</span>}
                </div>
              )}
              {tab === 'readme' && (
                <div className="whitespace-pre-wrap leading-relaxed">
                  {selected.readme ? selected.readme.slice(0, 80_000) : <span className="text-muted-foreground">No README captured.</span>}
                  {selected.readme && selected.readme.length > 80_000 && (
                    <div className="mt-2 text-[10px] text-muted-foreground">… truncated at 80 KB.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
